import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Lock as LockIcon, User } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ACCOUNT_COLOR_PALETTE, type AccountColor } from '@/features/accounts/account-colors';
import {
  formatAccountLabel,
  isArchivedAccount,
  type Account,
} from '@/features/accounts/accounts.api';
import { ConversationAccountBanner } from '@/features/accounts/ConversationAccountBanner';
import { useAccountColors } from '@/features/accounts/useAccountColors';
import { useAccounts } from '@/features/accounts/useAccounts';
import { Composer } from '@/features/chat/Composer';
import { isResendable } from '@/features/chat/delivery-status';
import { ProfileSidebar } from '@/features/chat/ProfileSidebar';
import { shouldIgnoreConversationEscape } from '@/features/chat/escape-guard';
import { MessageList } from '@/features/chat/MessageList';
import { useThreadMessages } from '@/features/chat/useThreadMessages';
import {
  type Conversation,
  type PagedConversations,
} from '@/features/conversations/conversations.api';
import {
  CONVERSATIONS_ROOT,
  conversationsKeys,
} from '@/features/conversations/keys';
import { useReadConversation } from '@/features/conversations/useReadConversation';
import { avatarGradientClass } from '@/lib/avatar-gradient';
import { formatPhone } from '@/lib/format-phone';
import { cn } from '@/lib/utils';
import { usePrefsStore } from '@/store/prefs';
import { useUIStore, type ThreadSelection } from '@/store/ui';

import type { MessageAccountChip, ResendOption } from '@/features/chat/MessageBubble';
import type { Message } from '@/features/chat/chat.api';

/**
 * Right-column panel for a MERGED phone-thread — one customer, several shop
 * numbers, one interleaved timeline (feat/merged-phone-thread).
 *
 * Activated by `useUIStore.setSelectedThread({ key, memberIds })` (the
 * aggregate inbox's merged row does this); `ChatPanel` renders this instead
 * of `ChatPanelInner` while `selectedThread` is non-null. Escape closes it
 * (same guard as the single-conversation panel).
 *
 * Data sources — deliberately NO new endpoints beyond the thread messages:
 *   - messages: `useThreadMessages(thread.key)` (via `MessageList`'s thread
 *     mode) — `GET /api/v1/threads/{key}/messages`.
 *   - member conversation rows: the already-loaded conversations caches
 *     (list pages + detail slots), read via a SCOPED QueryCache
 *     subscription — event-type (`updated`/`removed`) AND key-prefix
 *     (`conversations` root) scoped, per the PR #20 render-loop discipline.
 *   - accounts / colors: the cache-warm `useAccounts` / `useAccountColors`.
 *
 * v1 semantics: the merge is a READ-TIME view. Sends target exactly one
 * member conversation, chosen by the account picker next to the send button:
 *   - default = the account of the NEWEST INBOUND message in the loaded
 *     window (that's who the customer talked to last), falling back to the
 *     connected member with the latest activity;
 *   - only members whose account is `connected` are offered;
 *   - quoting a message FORCES the send through the account that owns the
 *     quoted message (reply anchors are wamids scoped to one account↔customer
 *     chat) — the picker auto-switches and locks while the quote is armed.
 * The Composer instance is stable across account switches (draft survives):
 * its draft-reset effect keys on the thread key, not the target conversation.
 *
 * Customer-profile sidebar: ThreadPanel owns its column wrapper and renders
 * the SAME `ProfileSidebar` as a flex sibling (mirroring ChatPanel's
 * single-conversation branch — ChatPanel renders this component directly into
 * its flex-row section). It shares the `profileOpen` / `profileSide` prefs
 * with the single-conversation view and keys on the currently CHOSEN member
 * conversation (the send target the account picker controls), so the profile
 * query follows the picker while the customer-level identity card and
 * 游戏账号 stay put (they key on the phone). Unmounted while no target is
 * resolved yet (caches warming / no connected member).
 */
export function ThreadPanel({ thread }: { thread: ThreadSelection }): React.JSX.Element {
  const setSelectedThread = useUIStore((s) => s.setSelectedThread);
  const queryClient = useQueryClient();

  // The message being replied to (quote) + the operator's explicit account
  // pick (a member conversation id). Both reset when the thread switches —
  // ThreadPanel is NOT remounted per thread (no `key` prop in ChatPanel).
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [chosenConversationId, setChosenConversationId] = useState<string | null>(null);
  useEffect(() => {
    setReplyingTo(null);
    setChosenConversationId(null);
  }, [thread.key]);

  // ESC-to-close — mirrors ChatPanelInner's handler (see its doc comment;
  // the composer's own Escape handling stopPropagation()s first).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      if (shouldIgnoreConversationEscape(e)) return;
      setSelectedThread(null);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setSelectedThread]);

  // --- Member conversation rows from the conversations caches --------------
  //
  // Cache-read + scoped subscription (NOT a new query): the rows were loaded
  // by the inbox that opened this thread, and the WS projection keeps them
  // fresh. The subscription is scoped in BOTH dimensions (event type and key
  // prefix) — see ChatPanel's lock-tick comment and the PR #20 regression
  // suite for why an unscoped callback is a self-sustaining render loop.
  const [, setMembersTick] = useState(0);
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' && event.type !== 'removed') return;
      if (event.query.queryKey[0] !== CONVERSATIONS_ROOT) return;
      setMembersTick((t) => t + 1);
    });
    return unsubscribe;
  }, [queryClient]);

  // Re-read on every render (the tick drives re-renders).
  const members = collectMemberConversations(queryClient, thread.memberIds);

  const accountsQuery = useAccounts();
  const accountColors = useAccountColors();
  const accountsById = useMemo(() => {
    const map = new Map<string, Account>();
    for (const account of accountsQuery.data ?? []) {
      map.set(account.id, account);
    }
    return map;
  }, [accountsQuery.data]);

  // Members whose account is currently able to send. A hidden (archived)
  // account is never offered — the hub un-hides any account it reports
  // `connected`, so this is belt and braces.
  const connectedMembers = members.filter((m) => {
    const account = accountsById.get(m.account_id);
    return account?.status === 'connected' && !isArchivedAccount(account);
  });

  // --- Send target ----------------------------------------------------------

  // ThreadPanel needs the loaded window itself (not just MessageList) to
  // compute the default target. Same query key as MessageList's thread mode
  // — one cache entry, two observers, zero extra fetches.
  const threadMessagesQuery = useThreadMessages(thread.key);
  const loadedMessages: Message[] = useMemo(
    () => threadMessagesQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [threadMessagesQuery.data],
  );

  // Default: the member owning the newest INBOUND message in the loaded
  // window (pages are newest-first, so the first inbound hit is the newest),
  // provided that member's account is connected; else the connected member
  // with the latest activity; else the first connected member; else null.
  const defaultConversationId = useMemo(() => {
    const connectedIds = new Set(connectedMembers.map((m) => m.id));
    const newestInbound = loadedMessages.find(
      (m) => m.direction === 'inbound' && connectedIds.has(m.conversation_id),
    );
    if (newestInbound) return newestInbound.conversation_id;
    const byActivity = [...connectedMembers].sort((a, b) => {
      const ta = a.last_message_at ? Date.parse(a.last_message_at) : 0;
      const tb = b.last_message_at ? Date.parse(b.last_message_at) : 0;
      return tb - ta;
    });
    return byActivity[0]?.id ?? null;
  }, [loadedMessages, connectedMembers]);

  // A quote FORCES the owning conversation (reply anchors are per-account
  // wamids); otherwise the explicit pick wins over the default. The explicit
  // pick is validated against current membership so a stale pick (member
  // vanished from cache) falls back to the default.
  const quoteLocked = replyingTo !== null;
  const pickIsValid =
    chosenConversationId !== null &&
    members.some((m) => m.id === chosenConversationId);
  const targetConversationId = quoteLocked
    ? replyingTo.conversation_id
    : pickIsValid
      ? chosenConversationId
      : defaultConversationId;

  const targetMember = members.find((m) => m.id === targetConversationId);
  // For a quoted message owned by a conversation we don't have a row for
  // (server-side union can exceed the client's memberIds), fall back to the
  // message's own account_id so the picker label stays truthful.
  const targetAccountId =
    targetMember?.account_id ??
    (quoteLocked ? replyingTo.account_id ?? null : null);

  // --- Per-bubble account chip ----------------------------------------------

  const accountChipFor = useCallback(
    (message: Message): MessageAccountChip | null => {
      const accountId =
        message.account_id ??
        members.find((m) => m.id === message.conversation_id)?.account_id;
      if (!accountId) return null;
      const account = accountsById.get(accountId);
      const color = accountColors.get(accountId) ?? ACCOUNT_COLOR_PALETTE[0];
      return {
        label: account ? formatAccountLabel(account) : '未知账号',
        dotClassName: color.swatch,
      };
    },
    // `members` is re-collected per render but its CONTENT changes only on
    // cache ticks; deriving deps from the stable primitives keeps the chip
    // resolver from churning MessageList unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsById, accountColors, memberFingerprint(members)],
  );

  // --- Per-bubble cross-account resend targets --------------------------------
  //
  // Only a REAL (persisted) outbound row in a resendable status offers
  // 「用其他账号重发」: the other CONNECTED members of this thread, minus the
  // message's own conversation (that one is the plain Retry). Labels and
  // color dots match the account chips / picker exactly — one identity
  // system per account.
  const resendOptionsFor = useCallback(
    (message: Message): ResendOption[] | null => {
      // Outbound, persisted (ghost `optim-` rows keep their re-POST retry),
      // status accepted by `POST /messages/{id}/resend` — one shared
      // predicate, so the resendable set widens in one place.
      if (!isResendable(message)) return null;
      const options = connectedMembers
        .filter((m) => m.id !== message.conversation_id)
        .map((m) => {
          const account = accountsById.get(m.account_id);
          const color = accountColors.get(m.account_id) ?? ACCOUNT_COLOR_PALETTE[0];
          return {
            conversationId: m.id,
            accountId: m.account_id,
            label: account ? formatAccountLabel(account) : '未知账号',
            colorClass: color.swatch,
          };
        });
      return options.length > 0 ? options : null;
    },
    // Same dep discipline as `accountChipFor` above: `connectedMembers` is
    // re-derived per render from `members` + `accountsById`, so the stable
    // primitives (fingerprint + the maps) are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsById, accountColors, memberFingerprint(members)],
  );

  // --- Auto mark-read fan-out -----------------------------------------------
  //
  // Mirrors ConversationRow's single-conversation effect: while the thread
  // is open, every member needing attention gets its own read POST (the
  // endpoint is per-conversation; N members = N idempotent POSTs). The
  // optimistic patch zeroes the rows immediately, so the effect's dep
  // fingerprint collapses and it does not re-fire in a loop.
  const markRead = useReadConversation();
  const attentionIds = members
    .filter((m) => m.unread_count > 0 || m.marked_unread)
    .map((m) => m.id)
    .sort()
    .join(',');
  useEffect(() => {
    if (!attentionIds) return;
    for (const id of attentionIds.split(',')) {
      markRead.mutate({ conversationId: id });
    }
    // Deliberately NOT depending on `markRead` (identity changes per render
    // — same rationale and suppression as ConversationRow's effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attentionIds]);

  // --- Render ----------------------------------------------------------------

  // Customer-profile sidebar — same shared pref as the single-conversation
  // view (one toggle, one side, default applies to both). It keys on the
  // currently CHOSEN member conversation (the send target), so switching the
  // account picker re-points the profile query while the customer-level
  // identity card and 游戏账号 stay put. While no target is resolved (caches
  // warming / no
  // connected member) the sidebar stays unmounted rather than mounting with
  // a transient id.
  const profileOpen = usePrefsStore((s) => s.profileOpen);
  const profileSide = usePrefsStore((s) => s.profileSide);
  const profileConversationId = profileOpen ? targetConversationId : null;

  // Flex ROW fragment: [profile?] · thread column · [profile?] — ChatPanel
  // renders this directly into its flex-row section, mirroring the
  // single-conversation branch's wrapper approach (identical column classes).
  return (
    <>
      {profileConversationId && profileSide === 'left' && (
        <ProfileSidebar conversationId={profileConversationId} />
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ThreadHeader
          thread={thread}
          members={members}
          accountsById={accountsById}
          accountColors={accountColors}
          accountPicker={
            targetConversationId ? (
              <ThreadAccountPicker
                members={connectedMembers}
                accountsById={accountsById}
                accountColors={accountColors}
                value={targetConversationId}
                valueAccountId={targetAccountId}
                locked={quoteLocked}
                onPick={setChosenConversationId}
              />
            ) : null
          }
        />
        <MessageList
          threadKey={thread.key}
          onReply={(message) => {
            // Arm the quote AND persist the switch: the reply must travel
            // through the quoted message's own account, and keeping the pick
            // after send/cancel matches the "auto-switch" semantics.
            setReplyingTo(message);
            setChosenConversationId(message.conversation_id);
          }}
          accountChipFor={accountChipFor}
          resendOptionsFor={resendOptionsFor}
        />
        {targetConversationId ? (
          <Composer
            conversationId={targetConversationId}
            draftResetKey={thread.key}
            threadKey={thread.key}
            accountId={targetAccountId}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            replyHint={quoteLocked ? '引用回复须经原账号发送' : undefined}
          />
        ) : (
          // No connected member — one ConversationAccountBanner PER member
          // (spec 01 §3.5 / 06 §2 file table) instead of the old single grey
          // "没有已连接的账号" notice: each member can be down for a DIFFERENT
          // reason (one qr_required, another banned, ...), and the admin
          // actions (扫码/重扫/重连) need a specific account to act on anyway.
          <div
            data-testid="thread-no-sender"
            className="flex shrink-0 flex-col gap-1.5 px-6 pb-5 pt-2"
          >
            {members.map((member) => (
              <ConversationAccountBanner
                key={member.id}
                account={accountsById.get(member.account_id)}
              />
            ))}
          </div>
        )}
      </div>
      {profileConversationId && profileSide === 'right' && (
        <ProfileSidebar conversationId={profileConversationId} />
      )}
    </>
  );
}

// --- Header ------------------------------------------------------------------

interface ThreadHeaderProps {
  thread: ThreadSelection;
  members: Conversation[];
  accountsById: Map<string, Account>;
  accountColors: Map<string, AccountColor>;
  /** The send-account picker. It lives HERE rather than in the composer: on
   *  the composer row it squeezed the textarea hard enough to wrap the
   *  placeholder, and the header already carries the thread's account
   *  identity (the member dots) so it reads naturally beside them. */
  accountPicker: React.ReactNode;
}

function ThreadHeader({
  thread,
  members,
  accountsById,
  accountColors,
  accountPicker,
}: ThreadHeaderProps): React.JSX.Element {
  const profileOpen = usePrefsStore((s) => s.profileOpen);
  const toggleProfile = usePrefsStore((s) => s.toggleProfile);

  // All members share the same customer (that's the thread's definition) —
  // any member's contact works; the key itself is the fallback while the
  // caches warm up.
  const contact = members[0]?.contact;
  const hasPhone = !!contact?.phone_e164;
  const contactName =
    contact?.display_name?.trim() || contact?.phone_e164 || contact?.peer_id || thread.key;
  const primaryText = hasPhone ? formatPhone(contact?.phone_e164) : contactName;
  const showNameRow = hasPhone && !!contact?.display_name?.trim();

  return (
    <header className="relative z-[2] flex shrink-0 items-center gap-3 px-6 py-3 shadow-neu-header">
      <Avatar className="size-11 shadow-neu-flat">
        <AvatarFallback
          className={cn(
            'text-sm font-extrabold text-white',
            avatarGradientClass(contact?.id ?? thread.key),
          )}
        >
          {computeInitials(contactName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'truncate text-[15.5px] font-extrabold',
              hasPhone && 'tabular-nums',
            )}
          >
            {primaryText}
          </span>
          <Badge variant="outline" className="shrink-0 rounded-lg border-transparent px-2 py-0.5 text-[9.5px] font-bold leading-none text-muted-foreground shadow-neu-inset-sm">
            合并会话
          </Badge>
        </div>
        {showNameRow && (
          <span className="truncate text-xs font-semibold text-muted-foreground">
            {contact?.display_name?.trim()}
          </span>
        )}
      </div>
      {/* One dot per member account — the shop numbers this customer has
          talked to, in member order. */}
      <div
        className="flex shrink-0 items-center gap-1.5"
        role="list"
        aria-label="会话成员账号"
      >
        {members.map((member) => {
          const account = accountsById.get(member.account_id);
          const color =
            accountColors.get(member.account_id) ?? ACCOUNT_COLOR_PALETTE[0];
          const label = account ? formatAccountLabel(account) : member.account_id;
          return (
            <span
              key={member.id}
              role="listitem"
              data-testid="thread-member-dot"
              title={label}
              aria-label={label}
              className={cn('size-2.5 shrink-0 rounded-full', color.swatch)}
            />
          );
        })}
      </div>
      {/* Send-account picker — moved off the composer row (see the prop). */}
      {accountPicker}
      {/* Customer-profile toggle — SAME button as ChatHeader's (shared
          profileOpen pref, identical open-state styling). Reads inset/active
          (aria-pressed) while the sidebar is open. */}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        aria-label="客户资料"
        aria-pressed={profileOpen}
        className={cn(
          'size-10 shrink-0 rounded-[13px]',
          profileOpen
            ? 'text-primary shadow-neu-inset-press'
            : 'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
        )}
        onClick={toggleProfile}
      >
        <User className="size-4" />
      </Button>
    </header>
  );
}

// --- Account picker ------------------------------------------------------------

interface ThreadAccountPickerProps {
  /** Members whose account is `connected` — the only legal targets. */
  members: Conversation[];
  accountsById: Map<string, Account>;
  accountColors: Map<string, AccountColor>;
  /** Currently-effective target member conversation id. */
  value: string;
  /** The effective target's account id (may resolve even when `value` is a
   *  non-member conversation — the quoted-message edge case). */
  valueAccountId: string | null;
  /** True while a quote is armed — the reply must go through the quoted
   *  message's account, so the picker is disabled with a hint. */
  locked: boolean;
  onPick: (conversationId: string) => void;
}

function ThreadAccountPicker({
  members,
  accountsById,
  accountColors,
  value,
  valueAccountId,
  locked,
  onPick,
}: ThreadAccountPickerProps): React.JSX.Element {
  const valueAccount = valueAccountId ? accountsById.get(valueAccountId) : undefined;
  const valueColor =
    (valueAccountId ? accountColors.get(valueAccountId) : undefined) ??
    ACCOUNT_COLOR_PALETTE[0];
  const triggerLabel = valueAccount
    ? formatAccountLabel(valueAccount)
    : '选择账号';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          disabled={locked}
          aria-label="选择发送账号"
          title={locked ? '引用回复须经原账号发送' : '选择发送账号'}
          className="flex h-10 shrink-0 items-center gap-1.5 rounded-[13px] px-2.5 text-[12px] font-bold shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press"
        >
            <span
              aria-hidden="true"
              data-testid="thread-picker-dot"
              className={cn('size-2 shrink-0 rounded-[3px]', valueColor.swatch)}
            />
            <span className="max-w-[7rem] truncate">{triggerLabel}</span>
            {locked ? (
              <LockIcon className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
            ) : (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {members.length === 0 ? (
          <DropdownMenuItem disabled>暂无已连接账号</DropdownMenuItem>
        ) : (
          members.map((member) => {
            const account = accountsById.get(member.account_id);
            const color =
              accountColors.get(member.account_id) ?? ACCOUNT_COLOR_PALETTE[0];
            return (
              <DropdownMenuItem key={member.id} onSelect={() => onPick(member.id)}>
                <span
                  aria-hidden="true"
                  className={cn('size-2 shrink-0 rounded-[3px]', color.swatch)}
                />
                <span className="truncate">
                  {account ? formatAccountLabel(account) : member.account_id}
                </span>
                {member.id === value && (
                  <span className="ml-auto text-[10px] text-muted-foreground">当前</span>
                )}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// --- Helpers -------------------------------------------------------------------

/** Shape stored in react-query for the conversations infinite lists. */
type InfiniteConversations = {
  pages: PagedConversations[];
  pageParams: unknown[];
};

/**
 * Resolve the thread's member conversation rows from the already-loaded
 * conversations caches. List pages win over detail slots (the lists are the
 * WS projection's primary patch surface, so they're at least as fresh).
 * Missing members are simply absent — the panel renders with what it has
 * and fills in as caches warm.
 */
function collectMemberConversations(
  queryClient: ReturnType<typeof useQueryClient>,
  memberIds: string[],
): Conversation[] {
  const wanted = new Set(memberIds);
  const found = new Map<string, Conversation>();

  for (const id of memberIds) {
    const detail = queryClient.getQueryData<Conversation>(
      conversationsKeys.detail(id),
    );
    if (detail) found.set(id, detail);
  }

  const lists = queryClient.getQueriesData<InfiniteConversations>({
    queryKey: conversationsKeys.lists(),
  });
  for (const [, data] of lists) {
    if (!data) continue;
    for (const page of data.pages) {
      for (const row of page.items) {
        if (wanted.has(row.id)) found.set(row.id, row);
      }
    }
  }

  return memberIds
    .map((id) => found.get(id))
    .filter((c): c is Conversation => c !== undefined);
}

/** Cheap content fingerprint for the members array — used as a memo dep so
 *  re-collecting an IDENTICAL member set per render doesn't churn memos. */
function memberFingerprint(members: Conversation[]): string {
  return members.map((m) => `${m.id}:${m.account_id}`).join('|');
}

/** Same initials logic as ChatPanel / ConversationRow — keep in sync. */
function computeInitials(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '??';
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) {
    return (tokens[0][0] + tokens[1][0]).toUpperCase();
  }
  return tokens[0].slice(0, 2).toUpperCase();
}
